import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { convertRecord } from '#/shared/utils/vehicles';
import { parseSignVanityName } from '#/shared/utils/transit';
import { countAlertsByRoute, mapRoute } from '#/transit-tracker/utils/routes';
import { CACHE_DURATION, TRIMET_FEED_ID } from '#/shared/constants';
import { mapAndSortArrivals } from '#/transit-tracker/utils/arrivals';

export const api = {
  host: 'https://developer.trimet.org',
  paths: {
    // TODO: finish building out the developerApi; all endpoints are setup
    //  and in use here and the migration is mostly complete, but the are
    //  still a couple of uses of `/arrivals` that use the old query approach.
    //  Once those go away this `api` object no longer needs to be exported
    alerts: '/ws/v3/alerts',
    arrivals: '/ws/v3/arrivals',
    vehicles: '/ws/v3/vehicles',
    stops: '/ws/v3/stops',
    routeConfig: '/ws/v3/routeConfig',
  },
  appId: '8CBD14D520C6026CC7EEE56A9',
};

const BASE_QUERY_PARAMS = {
  json: true,
  appId: api.appId,
};

export const DEFAULT_ARRIVALS_QUERY = {
  ...BASE_QUERY_PARAMS,
  minutes: 0,
  arrivals: 1,
};

export const developerApi = createApi({
  reducerPath: 'developerApi',
  baseQuery: fetchBaseQuery({ baseUrl: api.host }),
  refetchOnReconnect: true,
  endpoints: build => ({
    getRealtimeVehicles: build.query({
      // query only takes a single arg so if you have more than one pass it as an object
      query: rtvParams => ({
        url: api.paths.vehicles,
        params: { ...BASE_QUERY_PARAMS, ...rtvParams, hasTripId: true },
      }),
      // value in seconds to keep unsubscribed query in the cache
      keepUnusedDataFor: CACHE_DURATION.FIVE_SECONDS,
      // Pick out data and prevent nested properties in a hook or selector
      transformResponse: response => {
        return (response.resultSet?.vehicle || []).map(vehicle => {
          const parsedVehicle = convertRecord(vehicle);
          return {
            ...parsedVehicle,
            lastLocId: vehicle.lastLocID,
            nextLocId: vehicle.nextLocID,
            signMessage: vehicle.signMessageLong
              ? parseSignVanityName(
                  vehicle.signMessageLong,
                  vehicle.routeNumber
                )
              : null,
          };
        });
      },
    }),
    getAlerts: build.query({
      query: () => ({
        url: api.paths.alerts,
        params: BASE_QUERY_PARAMS,
      }),
      // value in seconds to keep unsubscribed query in the cache
      keepUnusedDataFor: CACHE_DURATION.ONE_MINUTE,
      transformResponse: response => {
        const alerts = response.resultSet.alert || [];
        const systemWideAlerts = alerts.filter(a => a.system_wide_flag);
        return {
          alertCountsByRoute: countAlertsByRoute(alerts),
          hasSystemWideAlert: systemWideAlerts && systemWideAlerts.length > 0,
          systemWideAlerts,
        };
      },
    }),
    getRouteAlerts: build.query({
      // query only takes a single arg so if you have more than one pass it as an object
      query: routeIdOrObject => {
        // Handle both old format { routeId, feedId } and new format routeId
        let routeParam;

        if (typeof routeIdOrObject === 'string') {
          // New format: "20" or "WAPARK:3433"
          routeParam = routeIdOrObject;
        } else if (
          routeIdOrObject.feedId &&
          routeIdOrObject.feedId !== TRIMET_FEED_ID
        ) {
          // Old format with non-TriMet feedId: construct "WAPARK:3433"
          routeParam = `${routeIdOrObject.feedId}:${routeIdOrObject.routeId}`;
        } else {
          // Old format with TriMet or no feedId: use just routeId
          routeParam = routeIdOrObject.routeId;
        }

        return {
          url: api.paths.alerts,
          params: {
            ...BASE_QUERY_PARAMS,
            route: routeParam,
          },
        };
      },
      // value in seconds to keep unsubscribed query in the cache
      keepUnusedDataFor: CACHE_DURATION.ONE_MINUTE,
      transformResponse: response => {
        const alerts =
          (response.resultSet.alert &&
            response.resultSet.alert.filter(a => !a.system_wide_flag)) ||
          [];
        return {
          routeAlerts: {
            alerts,
          },
        };
      },
    }),
    getStopAlerts: build.query({
      // query only takes a single arg so if you have more than one pass it as an object
      query: locIDs => ({
        url: api.paths.alerts,
        params: {
          ...BASE_QUERY_PARAMS,
          locIDs,
        },
      }),
      // value in seconds to keep unsubscribed query in the cache
      keepUnusedDataFor: CACHE_DURATION.ONE_MINUTE,
      transformResponse: response => {
        const alerts =
          (response.resultSet.alert &&
            response.resultSet.alert.filter(a => !a.system_wide_flag)) ||
          [];
        return {
          stopAlerts: {
            alerts,
          },
        };
      },
    }),
    getArrivals: build.query({
      query: arrivalsParams => ({
        url: api.paths.arrivals,
        params: {
          ...DEFAULT_ARRIVALS_QUERY,
          ...arrivalsParams,
        },
      }),
      keepUnusedDataFor: CACHE_DURATION.FIVE_SECONDS,
      transformResponse: response => response.resultSet.arrival,
    }),
    getStopArrivals: build.query({
      query: ({ stopIds = [], ...restQueryParams }) => ({
        url: api.paths.arrivals,
        params: {
          ...DEFAULT_ARRIVALS_QUERY,
          detailLevel: 2,
          locIDs: stopIds.join(','),
          ...restQueryParams,
        },
      }),
      keepUnusedDataFor: CACHE_DURATION.FIVE_SECONDS,
      transformResponse: response => {
        const {
          arrival: arrivals,
          location: stops,
          route: routes,
          detour: alerts,
        } = response.resultSet ?? {};

        if (!stops?.length) return null;

        return {
          arrivals: mapAndSortArrivals(arrivals, stops, routes),
          stops,
          routes,
          alerts,
        };
      },
    }),
    getNearbyStops: build.query({
      // query only takes a single arg so if you have more than one pass it as an object
      query: nearbyStopsParams => ({
        url: api.paths.stops,
        params: {
          ...BASE_QUERY_PARAMS,
          ...nearbyStopsParams,
        },
      }),
      // value in seconds to keep unsubscribed query in the cache
      keepUnusedDataFor: CACHE_DURATION.TEN_SECONDS,
      transformResponse: response => {
        return (response.resultSet.location || []).map(stop => ({
          ...stop,
          // TODO: `stopId` needs to be cast to string under PUBWEB-2237,
          //  using the util `mapStopToOtp` is one way to do this, but need
          //  explore other implications of that transformation
          stopId: stop.locid,
        }));
      },
    }),
    getRouteBasics: build.query({
      query: () => ({
        url: api.paths.routeConfig,
        params: BASE_QUERY_PARAMS,
      }),
      keepUnusedDataFor: CACHE_DURATION.ONE_HOUR,
      transformResponse: response => {
        // Handle different response structures between v1/v2 and v3
        const routes = response.resultSet?.route || response.route || [];

        if (!Array.isArray(routes)) {
          return {};
        }

        return routes.reduce(
          (routeLookup, routeData) => ({
            ...routeLookup,
            // Use composite key to prevent routes with same ID from different feeds from overwriting each other
            [`${routeData.feedId || TRIMET_FEED_ID}:${routeData.id}`]:
              mapRoute(routeData),
          }),
          {}
        );
      },
    }),
    getRouteDetails: build.query({
      query: routeDetailParams => ({
        url: api.paths.routeConfig,
        params: {
          ...BASE_QUERY_PARAMS,
          dir: true,
          ...routeDetailParams,
        },
      }),
      keepUnusedDataFor: CACHE_DURATION.FIVE_SECONDS,
      transformResponse: response => {
        // Handle different response structures between v1/v2 and v3
        const routes = response.resultSet?.route || response.route || [];
        const firstRoute = Array.isArray(routes) ? routes[0] : routes;
        return mapRoute(firstRoute);
      },
    }),
    getUniqueStopIdsByRoutes: build.query({
      query: routeDetailParams => ({
        url: api.paths.routeConfig,
        params: {
          ...BASE_QUERY_PARAMS,
          dir: true,
          stops: true,
          ...routeDetailParams,
        },
      }),
      keepUnusedDataFor: CACHE_DURATION.TEN_SECONDS,
      transformResponse: response => {
        // Handle different response structures between v1/v2 and v3
        const routes = response.resultSet?.route || response.route || [];

        if (!Array.isArray(routes) || routes.length === 0) {
          return [];
        }

        const uniqueIds = new Set();

        routes.forEach(route => {
          const feedId = route.feedId || TRIMET_FEED_ID;
          if (route && route.dir && Array.isArray(route.dir)) {
            route.dir.forEach(dir => {
              if (dir && dir.stop && Array.isArray(dir.stop)) {
                dir.stop.forEach(stop => {
                  if (stop && stop.locid) {
                    uniqueIds.add(`${feedId}:${stop.locid}`);
                  }
                });
              }
            });
          }
        });

        return Array.from(uniqueIds);
      },
    }),
  }),
});

export const {
  useGetRealtimeVehiclesQuery,
  useGetAlertsQuery,
  useGetRouteAlertsQuery,
  useGetStopAlertsQuery,
  useGetArrivalsQuery,
  useGetStopArrivalsQuery,
  useGetNearbyStopsQuery,
  useGetRouteBasicsQuery,
  useGetRouteDetailsQuery,
  useGetUniqueStopIdsByRoutesQuery,
} = developerApi;
